import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config";
import { Eye, ShoppingCart, Clock } from "lucide-react";

// Live activity bar shown at top of page
export const LiveActivityBar = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetch = () => axios.get(`${API}/social-proof`).then(r => setData(r.data)).catch(() => {});
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!data) return null;

  return (
    <div className="bg-[#0A0A0A] text-white py-2 overflow-hidden" data-testid="live-activity-bar">
      <div className="flex items-center justify-center gap-6 text-xs sm:text-sm animate-marquee">
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <Eye className="w-3.5 h-3.5" />
          <strong>{data.viewers}</strong> people shopping now
        </span>
        <span className="text-gray-600">|</span>
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <ShoppingCart className="w-3.5 h-3.5 text-[#FF6B00]" />
          <strong>{data.carts_today}</strong> items added to carts today
        </span>
        {data.orders_today > 0 && (
          <>
            <span className="text-gray-600">|</span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Clock className="w-3.5 h-3.5 text-green-400" />
              <strong>{data.orders_today}</strong> orders placed today
            </span>
          </>
        )}
      </div>
    </div>
  );
};

// Recent purchase popup notification
export const RecentPurchasePopup = () => {
  const [purchase, setPurchase] = useState(null);
  const [visible, setVisible] = useState(false);

  const fetchAndShow = useCallback(() => {
    axios.get(`${API}/social-proof`).then(r => {
      const purchases = r.data.recent_purchases || [];
      if (purchases.length > 0) {
        const random = purchases[Math.floor(Math.random() * purchases.length)];
        setPurchase(random);
        setVisible(true);
        setTimeout(() => setVisible(false), 5000);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const delay = setTimeout(fetchAndShow, 8000);
    const interval = setInterval(fetchAndShow, 45000);
    return () => { clearTimeout(delay); clearInterval(interval); };
  }, [fetchAndShow]);

  if (!visible || !purchase) return null;

  return (
    <div
      className="fixed bottom-20 sm:bottom-6 left-4 z-40 bg-white border-2 border-[#0A0A0A] shadow-xl p-3 max-w-xs animate-slide-in-left"
      data-testid="recent-purchase-popup"
    >
      <button onClick={() => setVisible(false)} className="absolute top-1 right-2 text-gray-400 text-xs">x</button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
          <ShoppingCart className="w-5 h-5 text-green-600" />
        </div>
        <div>
          <p className="text-xs font-bold text-[#0A0A0A] leading-tight">{purchase.product}</p>
          <p className="text-xs text-gray-500">Purchased from {purchase.location}</p>
          <p className="text-xs text-[#FF6B00] font-bold">{purchase.time_ago}</p>
        </div>
      </div>
    </div>
  );
};

// Product page urgency indicators
export const ProductUrgency = ({ stock, viewers }) => {
  const urgencyLevel = stock <= 3 ? "high" : stock <= 10 ? "medium" : "low";

  return (
    <div className="space-y-2" data-testid="product-urgency">
      {/* Live viewers */}
      <div className="flex items-center gap-2 text-sm">
        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
        <Eye className="w-4 h-4 text-gray-500" />
        <span className="text-gray-600"><strong>{viewers || Math.floor(Math.random() * 20) + 5}</strong> people viewing this</span>
      </div>

      {/* Stock urgency */}
      {urgencyLevel !== "low" && (
        <div className={`flex items-center gap-2 text-sm font-bold ${urgencyLevel === "high" ? "text-red-600" : "text-amber-600"}`}>
          <Clock className="w-4 h-4" />
          {urgencyLevel === "high"
            ? `Only ${stock} left! Order now before it's gone`
            : `Selling fast — only ${stock} remaining`
          }
        </div>
      )}
    </div>
  );
};
